import React, { useEffect, useMemo, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import {
  Alert,
  Box,
  Divider,
  FormControl,
  InputLabel,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import * as pdfjsLib from 'pdfjs-dist/legacy/build/pdf.mjs';

import { formatLengthToArchitecturalString, isUserVisibleException, useElementRect } from '@paragon/client-tools';
import { Point2D } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import { convertDegreesToRadians } from '@paragon/geometry-library';
import { hasPdfSegmentUploadAiScalingFeatureFlag } from '@paragon/login-tools';
import { Dialog } from '@paragon/popup-state';
import { getBoundsFromPoints } from '@paragon/three-shared/lib/getBoundsFromPoints';

import { persistNewSnappingUnderlay } from '../../store/actions/layout/snappingUnderlayActions';
import {
  snappingUnderlaySlice,
  SnappingUnderlayStatePendingDxf,
  SnappingUnderlayStatePendingPdf,
} from '../../store/slices/snappingUnderlaySlice';
import { DocumentRotationField } from '../controls/DocumentRotationField';
import { ScaleRatioField } from '../controls/ScaleRatioField';
import { useAppDispatch, useAppSelector } from '../hooks';

export function UnderlayUploadDialog() {
  const upload = useAppSelector((state) => state.snappingUnderlay.upload);

  switch (upload.status) {
    case 'loading':
      return <LoadingDialog />;

    case 'pending':
      return upload.sourceType === 'dxf' ? (
        <DxfConfigurationDialog state={upload} />
      ) : (
        <PdfConfigurationDialog state={upload} />
      );

    default:
      throw new Error(
        'Invalid UnderlayUploadDialog usage -- should only be rendered when state is loading or pending.',
      );
  }
}

function LoadingDialog() {
  return (
    <Dialog insistent variant="design">
      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'center' }}>
        <CloudUploadIcon sx={{ fontSize: '96px' }} />
      </Box>

      <Typography sx={{ marginY: 2 }}>Uploading and processing file...</Typography>

      <LinearProgress />
    </Dialog>
  );
}

const MAX_PREVIEW_WIDTH = 850;
const MAX_PREVIEW_HEIGHT = 750;
const SIDEBAR_WIDTH = 200;

// keep these values in sync with the enforcement in PdfsController
const MAX_PDF_PAGES = 30; // only enforced for AI scale extraction
const MAX_PDF_SIZE_BYTES = 30 * 1024 * 1024;

function computeRotatedFitScale(
  canvasSize: { width: number; height: number } | null,
  rotationDegrees: number,
  availableWidth: number,
  availableHeight: number,
): number {
  if (!canvasSize) return 1;

  const rad = convertDegreesToRadians(rotationDegrees);
  const cos = Math.abs(Math.cos(rad));
  const sin = Math.abs(Math.sin(rad));

  const rotatedWidth = canvasSize.width * cos + canvasSize.height * sin;
  const rotatedHeight = canvasSize.width * sin + canvasSize.height * cos;

  return Math.min(availableWidth / rotatedWidth, availableHeight / rotatedHeight, 1);
}

function RotatablePreviewCanvas({
  setContainerEl,
  setPreviewCanvasElement,
  rotation,
  scale,
}: {
  setContainerEl: (el: HTMLDivElement | null) => void;
  setPreviewCanvasElement: (el: HTMLCanvasElement | null) => void;
  rotation: number;
  scale: number;
}) {
  return (
    <Box
      ref={setContainerEl}
      sx={{
        maxWidth: MAX_PREVIEW_WIDTH,
        height: MAX_PREVIEW_HEIGHT,
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
        flex: '1 1 0',
        minWidth: 0,
      }}
    >
      <canvas
        ref={setPreviewCanvasElement}
        style={{
          display: 'block',
          transform: `rotate(${-rotation}deg) scale(${scale})`,
          transformOrigin: 'center center',
        }}
      />
    </Box>
  );
}

function RotationSidebarFooter({
  rotation,
  onRotationSubmit,
}: {
  rotation: number;
  onRotationSubmit: (rotation: number) => void;
}) {
  return (
    <>
      <DocumentRotationField value={rotation} onSubmit={onRotationSubmit} />

      <Divider sx={{ marginTop: 1 }} />

      <Typography variant="body2" color="text.secondary" sx={{ marginTop: 1, textAlign: 'center' }}>
        Scale and rotation can both be updated later.
      </Typography>
    </>
  );
}

function PdfConfigurationDialog({ state }: { state: SnappingUnderlayStatePendingPdf }) {
  const dispatch = useAppDispatch();
  const hasAiScalingFeatureFlag = useAppSelector(hasPdfSegmentUploadAiScalingFeatureFlag);

  const [previewCanvasElement, setPreviewCanvasElement] = useState<HTMLCanvasElement | null>(null);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number } | null>(null);
  const [previewContainerEl, setPreviewContainerEl] = useState<HTMLDivElement | null>(null);
  const previewContainerRect = useElementRect(previewContainerEl);

  const [numPages, setNumPages] = useState<number | null>(null);
  const [pdfSize, setPdfSize] = useState<number | null>(null);

  const availablePreviewWidth = Math.min(previewContainerRect?.width || MAX_PREVIEW_WIDTH, MAX_PREVIEW_WIDTH);
  const availablePreviewHeight = Math.min(previewContainerRect?.height || MAX_PREVIEW_HEIGHT, MAX_PREVIEW_HEIGHT);

  const failingValidationChecks = [];
  if (pdfSize != null && pdfSize > MAX_PDF_SIZE_BYTES) failingValidationChecks.push('too big');
  if (hasAiScalingFeatureFlag)
    if (numPages != null && numPages > MAX_PDF_PAGES) failingValidationChecks.push('too long');

  const [extractedPaths, setExtractedPaths] = useState<'loading' | 'error' | Point2D[][] | null>(null);
  const [extractionErrorMessage, setExtractionErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setExtractedPaths('loading');
    designService.pdfs
      .extractPaths(state.pdfUrl, state.previewPageNumber)
      .then(setExtractedPaths)
      .catch((error) => {
        console.error(error);
        setExtractionErrorMessage(isUserVisibleException(error) ? error.userVisibleMessage : null);
        setExtractedPaths('error');
      });
  }, [state.pdfUrl, state.previewPageNumber]);

  useEffect(() => {
    if (!state.pdfUrl) return;

    let cancelled = false;

    async function loadAndRenderPdf() {
      if (!previewCanvasElement || !state.pdfUrl) return;

      try {
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

        // FIXME: replace this once `Uint8Array.fromBase64` is widely supported
        const binaryString = atob(state.pdfUrl.split(',')[1]);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        setPdfSize(bytes.length);

        const pdf = await pdfjsLib.getDocument({ data: bytes }).promise;
        if (cancelled) return;

        setNumPages(pdf.numPages);

        const page = await pdf.getPage(state.previewPageNumber + 1); // pdf pages are technically 1-indexed
        if (cancelled) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const displayScale = Math.min(
          availablePreviewWidth / baseViewport.width,
          availablePreviewHeight / baseViewport.height,
        );
        const displayViewport = page.getViewport({ scale: displayScale });
        const outputScale = window.devicePixelRatio || 1;
        const canvasContext = previewCanvasElement.getContext('2d');

        if (!canvasContext) throw new Error('Unable to create the PDF preview canvas context.');

        previewCanvasElement.width = Math.ceil(displayViewport.width * outputScale);
        previewCanvasElement.height = Math.ceil(displayViewport.height * outputScale);
        previewCanvasElement.style.width = `${displayViewport.width}px`;
        previewCanvasElement.style.height = `${displayViewport.height}px`;
        setCanvasSize({ width: displayViewport.width, height: displayViewport.height });

        await page.render({
          canvas: previewCanvasElement,
          canvasContext,
          transform: outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0],
          viewport: displayViewport,
        }).promise;
      } catch (error) {
        console.error('Failed to load PDF:', error);
      }
    }

    loadAndRenderPdf();

    return () => {
      cancelled = true;
    };
  }, [state.pdfUrl, state.previewPageNumber, previewCanvasElement, availablePreviewWidth, availablePreviewHeight]);

  const previewScale = useMemo(
    () => computeRotatedFitScale(canvasSize, state.chosenRotation, availablePreviewWidth, availablePreviewHeight),
    [canvasSize, state.chosenRotation, availablePreviewWidth, availablePreviewHeight],
  );

  return (
    <Dialog
      insistent
      maxWidth="lg"
      variant="design"
      title="Upload PDF"
      onCancel={() => {
        dispatch(snappingUnderlaySlice.actions.entirelyUnloaded());
      }}
      submitText={extractedPaths === 'loading' ? 'Working...' : 'Upload'}
      submitDisabled={!Array.isArray(extractedPaths) || failingValidationChecks.length > 0}
      onSubmit={async () => {
        if (!Array.isArray(extractedPaths)) throw new Error('dialog submitted when disabled');

        await dispatch(
          persistNewSnappingUnderlay({
            extractedPaths,
            scale: state.chosenScale ?? 1,
            rotation: state.chosenRotation,
            fileName: state.pdfFileName,
            subIdentifier: `P${state.previewPageNumber + 1}`,
          }),
        );
      }}
      actionsNode={
        extractedPaths === 'error' ? (
          failingValidationChecks.length > 0 ? (
            <Alert variant="outlined" severity="warning">
              That PDF is too large to upload. Please extract the individual pages you are interested in and try again.
            </Alert>
          ) : (
            <Alert variant="outlined" severity="error">
              {extractionErrorMessage ??
                'Unable to extract line segments from PDF. Please contact Paragon Customer Support.'}
            </Alert>
          )
        ) : null
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <RotatablePreviewCanvas
          setContainerEl={setPreviewContainerEl}
          setPreviewCanvasElement={setPreviewCanvasElement}
          rotation={state.chosenRotation}
          scale={previewScale}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: SIDEBAR_WIDTH, flexShrink: 0 }}>
          <FormControl margin="normal" fullWidth>
            <InputLabel id="select-label" htmlFor="page-select">
              Page Number
            </InputLabel>
            <Select
              id="page-select"
              labelId="page-select-label"
              size="small"
              label="Page Number"
              value={state.previewPageNumber}
              onChange={(e) => {
                dispatch(snappingUnderlaySlice.actions.previewPageChosen(e.target.value as number));
              }}
            >
              {numPages != null &&
                Array.from({ length: numPages }, (_, i) => i).map((pageNum) => (
                  <MenuItem key={pageNum} value={pageNum}>
                    {pageNum + 1}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          <Divider />

          {(() => {
            const currentPageScales = state.extractedScales.find((item) => item.pageNumber === state.previewPageNumber);

            if (!currentPageScales || currentPageScales.scales.length === 0)
              return (
                <ScaleRatioField
                  scale={state.chosenScale ?? 1}
                  onSubmit={(scale) => {
                    dispatch(snappingUnderlaySlice.actions.scaleChosen(scale));
                  }}
                />
              );

            return (
              <>
                <Typography variant="subtitle1" sx={{ marginTop: 1 }}>
                  Configure Scale
                </Typography>

                <List dense sx={{ paddingY: 0 }}>
                  {currentPageScales.scales
                    .toSorted((a, b) => a - b)
                    .map((scale, index) => (
                      <ListItem key={index} dense>
                        <ListItemButton
                          selected={state.chosenScale != null && scale === state.chosenScale}
                          onClick={() => {
                            dispatch(snappingUnderlaySlice.actions.scaleChosen(scale));
                          }}
                        >
                          <ListItemText primary={`Scale = 1:${scale}`} secondary={formatRatio(scale)} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                </List>
              </>
            );
          })()}

          <RotationSidebarFooter
            rotation={state.chosenRotation}
            onRotationSubmit={(rotation) => {
              dispatch(snappingUnderlaySlice.actions.rotationChosen(rotation));
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
}

function formatRatio(scale: number): string {
  return `${formatLengthToArchitecturalString(12 / scale)} = 1'`;
}

const DXF_PREVIEW_MARGIN = 12;

function DxfConfigurationDialog({ state }: { state: SnappingUnderlayStatePendingDxf }) {
  const dispatch = useAppDispatch();

  const [previewCanvasElement, setPreviewCanvasElement] = useState<HTMLCanvasElement | null>(null);
  const [canvasSize, setCanvasSize] = useState<{ width: number; height: number } | null>(null);
  const [previewContainerEl, setPreviewContainerEl] = useState<HTMLDivElement | null>(null);
  const previewContainerRect = useElementRect(previewContainerEl);

  const availablePreviewWidth = Math.min(previewContainerRect?.width || MAX_PREVIEW_WIDTH, MAX_PREVIEW_WIDTH);
  const availablePreviewHeight = Math.min(previewContainerRect?.height || MAX_PREVIEW_HEIGHT, MAX_PREVIEW_HEIGHT);

  const currentLayout = state.layouts[state.previewPageNumber];
  const paths = currentLayout?.paths;

  useEffect(() => {
    if (!previewCanvasElement || !paths) return;

    const canvasContext = previewCanvasElement.getContext('2d');
    if (!canvasContext) return;

    const bounds = getBoundsFromPoints(paths.flat());

    const minX = bounds.position.x - bounds.size.width / 2;
    const minY = bounds.position.y - bounds.size.height / 2;
    const geometryWidth = bounds.size.width;
    const geometryHeight = bounds.size.height;

    const displayScale = Math.min(
      (availablePreviewWidth - 2 * DXF_PREVIEW_MARGIN) / geometryWidth,
      (availablePreviewHeight - 2 * DXF_PREVIEW_MARGIN) / geometryHeight,
    );

    const drawWidth = geometryWidth * displayScale + 2 * DXF_PREVIEW_MARGIN;
    const drawHeight = geometryHeight * displayScale + 2 * DXF_PREVIEW_MARGIN;
    const outputScale = window.devicePixelRatio || 1;

    previewCanvasElement.width = Math.ceil(drawWidth * outputScale);
    previewCanvasElement.height = Math.ceil(drawHeight * outputScale);
    previewCanvasElement.style.width = `${drawWidth}px`;
    previewCanvasElement.style.height = `${drawHeight}px`;
    setCanvasSize({ width: drawWidth, height: drawHeight });

    canvasContext.save();
    canvasContext.scale(outputScale, outputScale);
    canvasContext.clearRect(0, 0, drawWidth, drawHeight);
    canvasContext.strokeStyle = '#1976d2';
    canvasContext.lineWidth = 1;
    canvasContext.beginPath();

    for (const path of paths)
      path.forEach((point, index) => {
        const x = (point.x - minX) * displayScale + DXF_PREVIEW_MARGIN;
        // Flip Y: geometry is Y-up, canvas is Y-down.
        const y = drawHeight - ((point.y - minY) * displayScale + DXF_PREVIEW_MARGIN);
        if (index === 0) canvasContext.moveTo(x, y);
        else canvasContext.lineTo(x, y);
      });

    canvasContext.stroke();
    canvasContext.restore();
  }, [previewCanvasElement, paths, availablePreviewWidth, availablePreviewHeight]);

  const previewScale = useMemo(
    () => computeRotatedFitScale(canvasSize, state.chosenRotation, availablePreviewWidth, availablePreviewHeight),
    [canvasSize, state.chosenRotation, availablePreviewWidth, availablePreviewHeight],
  );

  return (
    <Dialog
      insistent
      maxWidth="lg"
      variant="design"
      title="Upload DXF"
      onCancel={() => {
        dispatch(snappingUnderlaySlice.actions.entirelyUnloaded());
      }}
      submitText="Upload"
      submitDisabled={!currentLayout}
      onSubmit={async () => {
        if (!currentLayout) throw new Error('dialog submitted when disabled');

        await dispatch(
          persistNewSnappingUnderlay({
            extractedPaths: currentLayout.paths,
            scale: 1,
            rotation: state.chosenRotation,
            fileName: state.fileName,
            subIdentifier: currentLayout.name,
          }),
        );
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <RotatablePreviewCanvas
          setContainerEl={setPreviewContainerEl}
          setPreviewCanvasElement={setPreviewCanvasElement}
          rotation={state.chosenRotation}
          scale={previewScale}
        />

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: SIDEBAR_WIDTH, flexShrink: 0 }}>
          <FormControl margin="normal" fullWidth>
            <InputLabel id="dxf-layout-select-label" htmlFor="dxf-layout-select">
              Layout
            </InputLabel>
            <Select
              id="dxf-layout-select"
              labelId="dxf-layout-select-label"
              size="small"
              label="Layout"
              value={state.previewPageNumber}
              onChange={(e) => {
                dispatch(snappingUnderlaySlice.actions.previewPageChosen(e.target.value as number));
              }}
            >
              {state.layouts.map((layout, index) => (
                <MenuItem key={index} value={index}>
                  {layout.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />

          <RotationSidebarFooter
            rotation={state.chosenRotation}
            onRotationSubmit={(rotation) => {
              dispatch(snappingUnderlaySlice.actions.rotationChosen(rotation));
            }}
          />
        </Box>
      </Box>
    </Dialog>
  );
}
